import { motion } from "framer-motion";
import { Loader2, Mail, Send } from "lucide-react";
import { useCurrency } from "../context/CurrencyContext";
import { useCheckout } from "../hooks/useCheckout";
import { TarotDeck } from "./TarotDeck";

const TAROT_IMG =
  "https://images.unsplash.com/photo-1674513000431-07beb07668d9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxjcnlzdGFsJTIwYmFsbCUyMGFsY2hlbXklMjBwb3Rpb24lMjBzdGFycyUyMG5pZ2h0JTIwc2t5JTIwdmludGFnZSUyMHBhcmNobWVudCUyMG1hZ2ljfGVufDB8fHx8MTc5MjUwMzQ2MXww&ixlib=rb-4.1.0&q=85";

export const Tarot = () => {
  const { format, formatAlt } = useCurrency();
  const { checkout, loading } = useCheckout();

  return (
    <section id="tarot" data-testid="tarot-section" className="relative py-28 sm:py-36 bg-ink2 border-y border-gold/10 overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9 }}
          >
            <p className="text-xs uppercase tracking-[0.35em] text-gold/80 font-mono mb-4">
              The Cards Speak
            </p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-parchment mb-6">
              Tarot Reading & Written Prediction
            </h2>
            <p className="text-base text-parchment-muted leading-relaxed max-w-lg mb-6">
              A full spread is drawn in your name under the correct planetary hour, and
              the reading is written out by hand — card by card, house by house — with
              the practitioner's interpretation and the workings advised for the
              season ahead.
            </p>
            <p className="text-sm text-parchment-muted leading-relaxed max-w-lg mb-8">
              Your prediction travels as you wish: sealed in an envelope by post, or
              swift as a raven by email. You will choose your destination after payment.
            </p>

            <div className="flex flex-wrap items-center gap-4 mb-10 text-xs font-mono tracking-wider text-parchment-muted">
              <span className="flex items-center gap-2 border border-gold/25 rounded-full px-4 py-2">
                <Send size={13} className="text-gold" /> By Post — sealed envelope
              </span>
              <span className="flex items-center gap-2 border border-gold/25 rounded-full px-4 py-2">
                <Mail size={13} className="text-gold" /> By Email — within 3 days
              </span>
            </div>

            <div className="flex flex-wrap items-end gap-8">
              <div>
                <span data-testid="tarot-price" className="font-display text-4xl sm:text-5xl text-gold text-glow-gold">
                  {format(15, 1200)}
                </span>
                <span className="block mt-2 text-xs font-mono text-parchment-muted">
                  ≈ {formatAlt(15, 1200)} · one full reading
                </span>
              </div>
              <button
                data-testid="razorpay-checkout-btn-tarot_reading"
                onClick={() => checkout("tarot_reading", "Tarot Reading & Written Prediction")}
                disabled={loading !== null}
                className="bg-gold text-ink text-xs uppercase tracking-[0.2em] font-semibold rounded-full px-8 py-4 hover:bg-amber transition-colors duration-300 disabled:opacity-60 flex items-center gap-2"
              >
                {loading === "tarot_reading" ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Opening Portal…
                  </>
                ) : (
                  "Draw My Cards"
                )}
              </button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="relative"
          >
            <div className="absolute -inset-3 border border-gold/25 rounded-2xl" aria-hidden="true" />
            <img
              src={TAROT_IMG}
              alt="Crystal sphere beneath a starlit sky"
              loading="lazy"
              className="relative rounded-2xl w-full h-[420px] lg:h-[520px] object-cover shadow-[0_0_60px_rgba(212,175,55,0.12)]"
            />
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-ink2/80 via-transparent to-transparent" />
            <p className="absolute bottom-5 left-6 font-quote italic text-parchment/90 text-sm">
              “The cards do not command fate — they illuminate the road.”
            </p>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
        >
          <TarotDeck />
        </motion.div>
      </div>
    </section>
  );
};
